import React from 'react';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { string } from 'yup';

import { useFormError, validator } from 'core/form/form';

import { authResetPasswordPhoneSelector } from 'state/auth/selectors';

import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';
import { LargeButton } from 'components/LargeButton/LargeButton';
import { IGNORE_ERROR, PasswordControlField } from 'components/PasswordControl/PasswordControl';
import { PasswordComplexity } from 'pages/Signup/components/PasswordComplexity';

import { ReactComponent as ResetPasswordIcon } from 'assets/glyphs/password.svg';

import { Form } from 'forms/types';

import css from './PasswordResetPasswordForm.module.css';

const passwordResetPasswordFormValueSelector = formValueSelector(Form.PASSWORD_RESET_PASSWORD_FORM);
const passwordSelector = (state: { password: string }) => passwordResetPasswordFormValueSelector(state, 'password');

export const PasswordResetPasswordForm = reduxForm({
    form: Form.PASSWORD_RESET_PASSWORD_FORM,
    initialValues: { password: '' },
    validate: validator({
        password: string()
            .required('common:validation.required')
            .matches(/[a-z]|[а-яё]/, IGNORE_ERROR)
            .matches(/[A-Z]|[А-ЯЁ]/, IGNORE_ERROR)
            .matches(/\d/, IGNORE_ERROR)
            .min(8, IGNORE_ERROR),
    }),
})(props => {
    const { handleSubmit } = props;

    const { t } = i18n.useTranslation();
    const error = useFormError(Form.PASSWORD_RESET_PASSWORD_FORM);
    const phone = useSelector(authResetPasswordPhoneSelector);
    const password = useSelector(passwordSelector);

    return (
        <form className={css.container} onSubmit={handleSubmit}>
            <div className={css.contents}>
                <Icon component={ResetPasswordIcon} size={64} svgSize={64} />
                <div className={css.title}>
                    <Text typography={TextTypography.DISPLAY_STRONG}>{t('auth:resetPassword.password.title')}</Text>
                </div>
                <div className={css.description}>
                    <Text component="div" typography={TextTypography.PARA}>
                        {t('auth:resetPassword.password.description')}
                    </Text>
                    <Text component="div" typography={TextTypography.PARA}>
                        {phone}
                    </Text>
                </div>
                <div className={css.password}>
                    <Field
                        name="password"
                        component={PasswordControlField}
                        label={t('auth:resetPassword.password.password')}
                        autoFocus={true}
                    />
                </div>
                <div className={css.description}>
                    <PasswordComplexity password={password} />
                </div>
                {error && (
                    <div className={css.error}>
                        <FormErrorMessage error={error} />
                    </div>
                )}
            </div>
            <div className={css.footer}>
                <div className={css.footerButton}>
                    <LargeButton type="submit">{t('auth:resetPassword.password.next')}</LargeButton>
                </div>
            </div>
        </form>
    );
});
